import dayjs from "dayjs";
import type { SocialProviderAccount } from "../hooks/useSocialProviderAccounts";
import type { PublicationModel, SocialProviderType } from "../types/models";
import type {
  OwnerAnalyticsCounts,
  OwnerAnalyticsDay,
  OwnerAnalyticsPayload,
  OwnerAnalyticsPost,
} from "./ownerAnalytics";
import { PublicationHelper } from "./PublicationHelper";
import { SLOT_PRIORITY } from "./publicationDistribution";
import { providerReportsStat, type PublicationStatKey } from "./publicationStats";

// Demo mode: the creator's own shorts laid out on the calendar, and analytics
// for the ones already "posted", so a demo of the product has something to
// show. Everything here is computed in the browser from the shorts and the
// clock. Nothing is written anywhere, and nothing is posted.

/** A short as the demo needs it (a `videos_v2` row, trimmed). */
export type DemoShort = {
  id: string;
  name: string;
  thumbnailUrl: string | null;
  createdAt: string;
  /** Per-provider captions, as the scheduler seeds posts with them. */
  publicationCaptions: Record<string, string | null> | null;
};

export const DEMO_PUBLICATION_ID_PREFIX = "demo-";

export const isDemoPublication = (publication: Pick<PublicationModel, "id">) =>
  publication.id.startsWith(DEMO_PUBLICATION_ID_PREFIX);

/** A short goes out to this many accounts at most. */
const MAX_DEMO_ACCOUNTS = 3;

// Deterministic randomness, so the numbers don't jump on every render:
// a string hash (FNV-1a) feeding mulberry32.
const seeded = (seed: string) => {
  let h = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  let state = h >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

/**
 * The accounts demo posts go to: the user's own connected accounts when
 * there are any, otherwise TikTok, Instagram and YouTube accounts named after
 * the user. The made-up ones only ever exist inside the demo scope.
 */
export function demoAccountsFor(
  accounts: readonly SocialProviderAccount[],
  identity: { name: string; avatarUrl: string },
): SocialProviderAccount[] {
  // Same rule as the publishing flow: an id-only placeholder or a dead token
  // can't post, so a demo post on it would wear "Reconnect".
  const connected = accounts.filter(
    (account) =>
      !account.needsReconnect &&
      !(account.name === account.id && account.username === account.id),
  );
  if (connected.length) return connected.slice(0, MAX_DEMO_ACCOUNTS);
  const handle =
    identity.name.toLowerCase().replace(/[^a-z0-9_.]/g, "") || "klapcreator";
  const providers: SocialProviderType[] = ["tiktok", "instagram", "youtube"];
  return providers.map((provider) => ({
    id: `${DEMO_PUBLICATION_ID_PREFIX}${provider}`,
    provider,
    name: identity.name || handle,
    username: provider === "youtube" ? `@${handle}` : handle,
    profilePicture: identity.avatarUrl,
  }));
}

// `count` posts spread over about a month (longer past 90), one to three a
// day: the day of the post `offset` places away from today (0 = the nearest),
// how many posts share that day, and its rank among them (0 = the nearest).
const spreadOver = (count: number) => {
  const days = Math.max(Math.min(30, count), Math.ceil(count / 3));
  const firstOf = (day: number) => Math.ceil((day * count) / days);
  return (offset: number) => {
    const day = Math.floor((offset * days) / count);
    return { day, perDay: firstOf(day + 1) - firstOf(day), rank: offset - firstOf(day) };
  };
};

/** The scheduler's preferred hours for `perDay` posts, earliest first. */
const hoursFor = (perDay: number) =>
  SLOT_PRIORITY.slice(0, perDay)
    .map((slot) => slot.preferredHour)
    .sort((a, b) => a - b);

/**
 * One post per short and account. The older half of the shorts (by creation)
 * went out before today, the rest are booked from today on, each half over
 * about a month at the scheduler's preferred hours. Today's posts whose time
 * has passed count as posted.
 */
export function buildDemoPublications({
  shorts,
  accounts,
  ownerId,
  now,
}: {
  shorts: readonly DemoShort[];
  accounts: readonly SocialProviderAccount[];
  ownerId: string;
  now: Date;
}): PublicationModel[] {
  const ordered = [...shorts].sort(
    (a, b) => a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id),
  );
  const pastCount = Math.ceil(ordered.length / 2);
  const placePast = spreadOver(pastCount);
  const placeBooked = spreadOver(ordered.length - pastCount);
  const today = dayjs(now).startOf("day");

  return ordered
    .flatMap((short, index) => {
      // Past shorts fill backwards from yesterday's last slot, the others
      // forwards from today's first.
      const past = index < pastCount;
      const { day, perDay, rank } = past
        ? placePast(pastCount - 1 - index)
        : placeBooked(index - pastCount);
      const hours = hoursFor(perDay);
      const scheduledAt = (
        past
          ? today.subtract(day + 1, "day").hour(hours[perDay - 1 - rank])
          : today.add(day, "day").hour(hours[rank])
      ).toDate();
      const createdAt = new Date(
        Math.min(new Date(short.createdAt).getTime(), scheduledAt.getTime()),
      );
      return accounts.map(
        (account): PublicationModel => ({
          id: `${DEMO_PUBLICATION_ID_PREFIX}${short.id}-${account.id}`,
          ownerId,
          accountId: account.id,
          provider: account.provider,
          createdAt,
          scheduledAt,
          postInfo: PublicationHelper.getDefaultPayloadForProvider(
            account.provider,
            short.publicationCaptions?.[account.provider] || short.name,
          ),
          videoUrl: null,
          projectId: short.id,
          autoFolderId: null,
          status: scheduledAt <= now ? "published" : "scheduled",
          publishedUrl: null,
          exportId: null,
          errorCode: null,
        }),
      );
    })
    .sort(
      (a, b) =>
        b.scheduledAt.getTime() - a.scheduledAt.getTime() || a.id.localeCompare(b.id),
    );
}

const COUNTERS = [
  ["views", "viewCount"],
  ["likes", "likeCount"],
  ["comments", "commentCount"],
  ["shares", "shareCount"],
  ["saves", "saveCount"],
] as const satisfies readonly (readonly [keyof OwnerAnalyticsCounts, PublicationStatKey])[];

type Counter = (typeof COUNTERS)[number][0];

type DemoCurve = {
  publication: PublicationModel;
  short: DemoShort | undefined;
  /** The counter at a moment, unrounded; null when the platform doesn't report it. */
  at: (counter: Counter, time: number) => number | null;
};

const DAY_MS = 24 * 60 * 60 * 1000;

// Each post heads for 10k-50k views: three quarters in a burst over its first
// day or so, the rest a tail over the following weeks, so every post keeps
// climbing. Later shorts trend higher, so the daily chart climbs too. The
// other counters are fixed shares of the views.
const curveFor = (
  publication: PublicationModel,
  short: DemoShort | undefined,
  rank: number,
): DemoCurve => {
  const random = seeded(publication.id);
  const target = 10_000 + 40_000 * (0.55 * rank + 0.45 * random());
  const burstMs = (0.4 + 0.8 * random()) * DAY_MS;
  const tailMs = (6 + 6 * random()) * DAY_MS;
  const rates: Record<Counter, number> = {
    views: 1,
    likes: 0.035 + 0.05 * random(),
    comments: 0.004 + 0.008 * random(),
    shares: 0.003 + 0.012 * random(),
    saves: 0.004 + 0.012 * random(),
  };
  const publishedAt = publication.scheduledAt.getTime();
  return {
    publication,
    short,
    at: (counter, time) => {
      const key = COUNTERS.find(([name]) => name === counter)![1];
      if (!providerReportsStat(publication.provider, key)) return null;
      const age = Math.max(0, time - publishedAt);
      const reached =
        0.75 * (1 - Math.exp(-age / burstMs)) + 0.25 * (1 - Math.exp(-age / tailMs));
      return target * reached * rates[counter];
    },
  };
};

const countsOf = (value: (counter: Counter) => number | null): OwnerAnalyticsCounts =>
  Object.fromEntries(COUNTERS.map(([counter]) => [counter, value(counter)])) as OwnerAnalyticsCounts;

const roundOrNull = (value: number | null) =>
  value === null ? null : Math.round(value);

/**
 * What `get_owner_analytics` would answer if the demo posts had been
 * measured just now: same window rules (days end with yesterday, the window
 * before it gives the growth), same rounding, same post list and order.
 */
export function buildDemoAnalytics({
  publications,
  shorts,
  now,
  tz,
  days,
  publicationId,
}: {
  publications: readonly PublicationModel[];
  shorts: readonly DemoShort[];
  now: Date;
  tz: string;
  days: number | null;
  publicationId: string | null;
}): OwnerAnalyticsPayload {
  const shortsById = new Map(shorts.map((short) => [short.id, short]));
  const rankOf = new Map(
    [...shorts]
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id))
      .map((short, index, all) => [short.id, all.length > 1 ? index / (all.length - 1) : 1]),
  );
  const nowMs = now.getTime();
  const published = publications.filter(
    (publication) => publication.scheduledAt.getTime() <= nowMs,
  );
  const scoped = published
    .filter((publication) => !publicationId || publication.id === publicationId)
    .map((publication) =>
      curveFor(
        publication,
        shortsById.get(publication.projectId ?? ""),
        rankOf.get(publication.projectId ?? "") ?? 1,
      ),
    );

  const endDay = dayjs(now).startOf("day").subtract(1, "day");
  const windowDays = days === null ? null : Math.min(Math.max(days, 1), 3660);
  const from = windowDays === null ? null : endDay.subtract(windowDays - 1, "day");
  const previousFrom =
    windowDays === null ? null : endDay.subtract(2 * windowDays - 1, "day");
  // Day boundaries as instants: a window covers [start of first, end of last].
  const windowStart = from?.valueOf() ?? -Infinity;
  const windowEnd = endDay.add(1, "day").valueOf();
  const previousStart = previousFrom?.valueOf() ?? -Infinity;

  const reported = countsOf((counter) =>
    scoped.some((curve) => curve.at(counter, nowMs) !== null) ? 0 : null,
  );
  const gainBetween = (curve: DemoCurve, counter: Counter, start: number, end: number) => {
    const startValue = curve.at(counter, start === -Infinity ? 0 : start);
    const endValue = curve.at(counter, end);
    return startValue === null || endValue === null ? null : endValue - startValue;
  };

  // Daily gains of every scoped post, summed, then rounded cumulatively so
  // the days add up to the totals.
  const seriesStart =
    from ??
    (scoped.length
      ? dayjs(
          Math.min(...scoped.map((curve) => curve.publication.scheduledAt.getTime())),
        ).startOf("day")
      : null);
  const series: OwnerAnalyticsDay[] = [];
  if (seriesStart && !seriesStart.isAfter(endDay, "day")) {
    const running = countsOf(() => 0) as Record<Counter, number>;
    const rounded = countsOf(() => 0) as Record<Counter, number>;
    for (let day = seriesStart; !day.isAfter(endDay, "day"); day = day.add(1, "day")) {
      const start = day.valueOf();
      const end = day.add(1, "day").valueOf();
      const point = { day: day.format("YYYY-MM-DD") } as OwnerAnalyticsDay;
      for (const [counter] of COUNTERS) {
        for (const curve of scoped) {
          running[counter] += gainBetween(curve, counter, start, end) ?? 0;
        }
        const next = Math.round(running[counter]);
        point[counter] = next - rounded[counter];
        rounded[counter] = next;
      }
      series.push(point);
    }
  }

  const posts = scoped
    .map((curve): OwnerAnalyticsPost & { gainInWindow: boolean } => {
      const latest = countsOf((counter) => roundOrNull(curve.at(counter, nowMs)));
      const gain =
        windowDays === null
          ? latest
          : countsOf((counter) =>
              roundOrNull(gainBetween(curve, counter, windowStart, windowEnd)),
            );
      const { publication, short } = curve;
      return {
        publication_id: publication.id,
        provider: publication.provider,
        account_id: publication.accountId,
        project_id: publication.projectId,
        published_at: publication.scheduledAt.toISOString(),
        measured_at: now.toISOString(),
        label: null,
        published_url: null,
        video_name: short?.name ?? null,
        thumbnail_url: short?.thumbnailUrl ?? null,
        video_url: null,
        latest,
        gain,
        gainInWindow: COUNTERS.some(([counter]) => (gain[counter] ?? 0) > 0),
      };
    })
    // A window lists the posts published since it started (today's too) and
    // those that gained anything in it.
    .filter(
      (post) =>
        windowDays === null ||
        publicationId !== null ||
        new Date(post.published_at).getTime() >= windowStart ||
        post.gainInWindow,
    )
    .map(({ gainInWindow: _, ...post }) => post)
    .sort(
      (a, b) =>
        (b.gain.views ?? -1) - (a.gain.views ?? -1) ||
        b.published_at.localeCompare(a.published_at) ||
        a.publication_id.localeCompare(b.publication_id),
    );

  const sumOf = (value: (curve: DemoCurve, counter: Counter) => number | null) =>
    countsOf((counter) =>
      reported[counter] === null
        ? null
        : Math.round(
            scoped.reduce((sum, curve) => sum + (value(curve, counter) ?? 0), 0),
          ),
    );

  return {
    tz,
    from: from?.format("YYYY-MM-DD") ?? null,
    to: endDay.format("YYYY-MM-DD"),
    has_data: published.length > 0,
    published_count: scoped.filter((curve) => {
      const time = curve.publication.scheduledAt.getTime();
      return windowDays === null || (time >= windowStart && time < windowEnd);
    }).length,
    totals:
      windowDays === null
        ? sumOf((curve, counter) => curve.at(counter, nowMs))
        : sumOf((curve, counter) => gainBetween(curve, counter, windowStart, windowEnd)),
    previous_totals:
      windowDays === null
        ? null
        : sumOf((curve, counter) =>
            gainBetween(curve, counter, previousStart, windowStart),
          ),
    series,
    posts,
  };
}
